import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { FiRefreshCw, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';

const BADGE_TIPO = {
  presupuesto: 'badge-info',
  factura: 'badge-ok',
  cita: 'badge-warning',
};

function fmtFechaLarga(f) {
  if (!f) return '—';
  return new Date(f).toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'short' });
}

export default function EmailLogSession({ sesion }) {
  const [email, setEmail] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [reintentando, setReintentando] = useState(false);
  const [mensajeReintento, setMensajeReintento] = useState(null);

  const cargarEmail = async () => {
    setCargando(true);
    try {
      const { data } = await api.get(`/email-logs/${sesion.entityId}`);
      setEmail(data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar el email.');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarEmail();
  }, [sesion.entityId]);

  const onReintentar = async () => {
    setReintentando(true);
    setMensajeReintento(null);
    try {
      const { data } = await api.post(`/email-logs/${sesion.entityId}/reintentar`);
      setEmail(data);
      if (data.estado === 'enviado') {
        setMensajeReintento({ tipo: 'ok', texto: '¡Reintento exitoso! El email ha sido enviado correctamente.' });
      } else {
        setMensajeReintento({ tipo: 'error', texto: `El reintento falló: ${data.errorMsg || 'error de conexión SMTP'}` });
      }
    } catch (err) {
      setMensajeReintento({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo reintentar el envío.' });
    } finally {
      setReintentando(false);
    }
  };

  if (cargando) return <div className="session-content"><p className="empty">Cargando…</p></div>;
  if (error) return <div className="session-content"><div className="alert-error">{error}</div></div>;
  if (!email) return null;

  return (
    <div className="session-content">
      {/* Cabecera */}
      <div className="session-panel" style={{ marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <tbody>
            {[
              ['De', email.enviadoPor ? `${email.enviadoPor.nombre} ${email.enviadoPor.apellidos || ''}`.trim() : 'Sistema'],
              ['Para', email.para || '—'],
              ['CC', email.cc || '—'],
              ['Asunto', email.asunto || '—'],
              ['Fecha', fmtFechaLarga(email.createdAt)],
              ['Intentos', `${email.intentos || 1} intento(s)`],
              ['Último reintento', email.ultimoReintentoAt ? fmtFechaLarga(email.ultimoReintentoAt) : 'Sin reintentos'],
            ].map(([label, valor]) => (
              <tr key={label}>
                <td style={{ color: 'var(--text-soft)', paddingBottom: 6, paddingRight: 16, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{label}</td>
                <td style={{ paddingBottom: 6 }}>{valor}</td>
              </tr>
            ))}
            <tr>
              <td style={{ color: 'var(--text-soft)', paddingBottom: 6, paddingRight: 16, fontWeight: 600, fontSize: 12 }}>Tipo</td>
              <td style={{ paddingBottom: 6 }}>
                <span className={`badge ${BADGE_TIPO[email.tipo] || 'badge'}`}>{email.tipo || '—'}</span>
              </td>
            </tr>
            <tr>
              <td style={{ color: 'var(--text-soft)', paddingBottom: 6, paddingRight: 16, fontWeight: 600, fontSize: 12 }}>Estado</td>
              <td style={{ paddingBottom: 6 }}>
                <span className={`badge ${email.estado === 'enviado' ? 'badge-ok' : email.estado === 'error' ? 'badge-danger' : 'badge'}`}>
                  {email.estado || '—'}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Alerta de error de envío */}
      {email.errorMsg && (
        <div className="alert-error" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <FiAlertTriangle style={{ verticalAlign: '-2px', marginRight: 6 }} />
            <strong>Error al enviar:</strong> {email.errorMsg}
          </div>
          <button
            type="button"
            className="btn btn-primary mini"
            onClick={onReintentar}
            disabled={reintentando}
          >
            <FiRefreshCw className={reintentando ? 'spin' : ''} /> {reintentando ? 'Reintentando…' : 'Reintentar envío ahora'}
          </button>
        </div>
      )}

      {mensajeReintento && (
        <div className={`alert-${mensajeReintento.tipo}`} style={{ marginBottom: 16 }}>
          {mensajeReintento.texto}
        </div>
      )}

      {/* Cuerpo del email */}
      {email.cuerpoHtml ? (
        <div
          dangerouslySetInnerHTML={{ __html: email.cuerpoHtml }}
          style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 20, background: '#fff', fontSize: 14, lineHeight: 1.6 }}
        />
      ) : (
        <pre style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 20, background: '#fff', fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: 0 }}>
          {email.cuerpoTexto || '(Sin contenido)'}
        </pre>
      )}
    </div>
  );
}
